if (window.__INITIAL_STATE__.glb) {
    window.__INITIAL_STATE__ = window.__INITIAL_STATE__.glb;
}

import { markRaw } from 'vue';
import { createCSR, createSSR } from './app.js';
import { fnExt } from '@/common/utilities/jqext';
import initialClient from '@/common/utilities/initialclient';
import eventDrag from '@/common/utilities/jqext/event.drag';
import registerDirectives from '@/common/directives';
import { populateStoreForPageType } from './entry-common.js';
import windowPolyfill from '@/common/utilities/window.polyfill.js';

/**
 * Recursively marks every object/array in a tree as non-reactive (Vue skips
 * Proxy wrapping).  Applied to read-only server data before it enters the
 * Vuex store so Vue doesn't spend time deep-proxying thousands of display-only
 * properties that will never change after initial hydration.
 */
function deepMarkRaw(obj, seen) {
    if (obj == null || typeof obj !== 'object') return obj;
    if (!seen) seen = new WeakSet();
    if (seen.has(obj)) return obj;
    seen.add(obj);
    markRaw(obj);
    if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
            deepMarkRaw(obj[i], seen);
        }
    } else {
        const keys = Object.keys(obj);
        for (let i = 0; i < keys.length; i++) {
            deepMarkRaw(obj[keys[i]], seen);
        }
    }
    return obj;
}

$.fn.extend(fnExt);
eventDrag($);
windowPolyfill(window);

/**
 * Dynamically imports and registers the page-specific Vuex store module.
 * The client store only ships `glb` eagerly; everything else is loaded on
 * demand so the main bundle stays small.
 */
const storeModuleMap = {
    vmapsearch:      { name: 'msp',             loader: () => import(/* webpackChunkName: "store-msp" */             './common/store/msp/index.js') },
    vsearchstate:    { name: 'searchState',      loader: () => import(/* webpackChunkName: "store-searchstate" */     './common/store/searchstate/index.js') },
    vdpp:            { name: 'dpp',              loader: () => import(/* webpackChunkName: "store-dpp" */             './common/store/dpp/index.js') },
    vhome:           { name: 'home',             loader: () => import(/* webpackChunkName: "store-home" */            './common/store/home/index.js') },
    vbrokerage:      { name: 'agents',           loader: () => import(/* webpackChunkName: "store-agents" */          './common/store/agents/index.js') },
    vagentsearch:    { name: 'agents',           loader: () => import(/* webpackChunkName: "store-agents" */          './common/store/agents/index.js') },
    vagentsearchzip: { name: 'agents',           loader: () => import(/* webpackChunkName: "store-agents" */          './common/store/agents/index.js') },
    vdap:            { name: 'dap',              loader: () => import(/* webpackChunkName: "store-dap" */             './common/store/dap/index.js') },
    vdsp:            { name: 'dsp',              loader: () => import(/* webpackChunkName: "store-dsp" */             './common/store/dsp/index.js') },
    vabout:          { name: 'about',            loader: () => import(/* webpackChunkName: "store-about" */           './common/store/about/index.js') },
    vsurvey:         { name: 'survey',           loader: () => import(/* webpackChunkName: "store-survey" */          './common/store/survey/index.js') },
    vsurveymortgage: { name: 'surveyMortgage',   loader: () => import(/* webpackChunkName: "store-surveymortgage" */ './common/store/surveymortgage/index.js') },
    vgeolanding:     { name: 'geolanding',       loader: () => import(/* webpackChunkName: "store-geolanding" */     './common/store/geolanding/index.js') },
    vneighborhood:   { name: 'neighborhood',     loader: () => import(/* webpackChunkName: "store-neighborhood" */   './common/store/neighborhood/index.js') },
};

// pageTypes that match by prefix (regex-tested in entry-common.js)
const prefixModuleMap = [
    { prefix: 'vguide',          name: 'guide',    loader: () => import(/* webpackChunkName: "store-guide" */    './common/store/guide/index.js') },
    { prefix: 'vsitemap',        name: 'sitemap',   loader: () => import(/* webpackChunkName: "store-sitemap" */  './common/store/sitemap/index.js') },
    { prefix: 'vschools',        name: 'schools',   loader: () => import(/* webpackChunkName: "store-schools" */  './common/store/schools/index.js') },
    { prefix: 'vmarket-trends',  name: 'market',    loader: () => import(/* webpackChunkName: "store-market" */   './common/store/markettrends/index.js') },
    { prefix: 'vmymovoto',       name: 'mymovoto',  loader: () => import(/* webpackChunkName: "store-mymovoto" */ './common/store/mymovoto/index.js') },
];

// Additional exact-match modules not covered above
const additionalExactMap = {
    vsell:                      { name: 'sell',    loader: () => import(/* webpackChunkName: "store-sell" */    './common/store/sell/index.js') },
    vsell_cities:               { name: 'sell',    loader: () => import(/* webpackChunkName: "store-sell" */    './common/store/sell/index.js') },
    verror:                     { name: 'home',    loader: () => import(/* webpackChunkName: "store-home" */    './common/store/home/index.js') },
    vhomeowner:                 { name: 'mymovoto', loader: () => import(/* webpackChunkName: "store-mymovoto" */ './common/store/mymovoto/index.js') },
    vwidgethearstmarketinsight: { name: 'widgets', loader: () => import(/* webpackChunkName: "store-widgets" */ './common/store/widgets/index.js') },
};

async function registerStoreModules(store, pageType) {
    const loaders = [];

    // 1. Exact match
    const exact = storeModuleMap[pageType];
    if (exact) {
        loaders.push(exact);
    }

    // 2. Prefix match (for vguide*, vsitemap*, etc.)
    if (!exact) {
        for (const pm of prefixModuleMap) {
            if (pageType.startsWith(pm.prefix)) {
                loaders.push(pm);
                break;
            }
        }
    }

    // 3. Additional exact matches (e.g. vsell_cities → sell)
    const additional = additionalExactMap[pageType];
    if (additional && !loaders.find(l => l.name === additional.name)) {
        loaders.push(additional);
    }

    // Import and register all needed modules in parallel
    const results = await Promise.all(
        loaders.map(async ({ name, loader }) => {
            const mod = await loader();
            return { name, mod: mod.default };
        })
    );

    for (const { name, mod } of results) {
        if (!store.hasModule(name)) {
            store.registerModule(name, mod);
        }
    }
}

async function mountApp() {
    if (window.__INITIAL_STATE__) {
        // The EJS template now uses <script type="application/json"> + JSON.parse()
        // which produces clean data without the per-field HTML entity
        // escape/unescape round-trip that initialParse previously handled.
        const context = window.__INITIAL_STATE__;

        // see vweb/common/controllers/vuecontroller.js for where the context.ssr object is populated by the server
        const shouldHydrate = context?.ssr && context?.ssr?.useSSR && context?.ssr?.shouldHydrate;
        const { router, app, store } = shouldHydrate ? createSSR(null) : createCSR(null);

        initialClient(context);

        // Mark the biggest read-only blobs as non-reactive BEFORE they enter
        // the store.  initalGlbState does Object.assign(state, context) which
        // would otherwise deep-proxy every nested object in these trees.
        //
        // pageData: entire server response (listings, market, schools, geo
        //   content, nearby data, etc.) — typically 80-90% of the payload.
        //   Display-only, replaced wholesale on each search.
        // splits: feature-flag dictionary (50-100+ keys, all primitives).
        //   Read-only after init — never mutated client-side.
        if (context.pageData) deepMarkRaw(context.pageData);
        if (context.splits) markRaw(context.splits);

        store.commit('glb/initalGlbState', context);

        // Dynamically register the store module(s) needed for this page.
        await registerStoreModules(store, context.pageType);

        registerDirectives(app);
        populateStoreForPageType(store, app);

        await router.isReady();
        app.mount('#app');
    }
}

mountApp();

export default mountApp;
